import { cn } from '@crowdex/ui/lib/utils';
import { PanelLeft, Plus, Search } from 'lucide-react';
import type { ReactNode } from 'react';
import { CrowlingoSwitcher } from '@/components/desktop-shell/crowlingo-switcher';
import { useAppSidebarSide } from '@/components/desktop-shell/sidebar-side-toggle';
import { AccountMenu, useAccountsToReconnect } from '@/components/sidebar/account-menu';
import { setAllAppsOpen } from '@/components/sidebar/all-apps';
import { FavoriteTiles } from '@/components/sidebar/favorite-tiles';
import { openShellSearch } from '@/components/sidebar/search-dialog';
import { useShellNavigation } from '@/components/sidebar/shell-navigation';
import { NEW_TASK_SHORTCUT, TOGGLE_SIDEBAR_SHORTCUT } from '@/components/sidebar/shell-shortcuts';
import { WorkspaceBody } from '@/components/sidebar/workspace-body';
import { NotificationsButton } from '@/features/notifications/notifications-button';
import { SIDEBAR_WIDTH } from '@/hooks/use-desktop-sidebar';
import { useWorkspaceDirectory } from '@/lib/assistants/directory';
import { SIDEBAR_SEARCH_SHORTCUT } from '@/lib/desktop-shell/sidebar-search';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { useLibraryPage } from '@/lib/navigation';
import { commandLabel, useShortcutOverrides } from '@/lib/settings-page/keyboard-shortcuts';

/** Where the hidden sidebar is revealed: a pointer resting on the window's left edge. */
export function DesktopSidebarChrome({
  hoverTargetVisible,
  revealSidebar,
}: {
  hoverTargetVisible: boolean;
  revealSidebar: () => void;
}) {
  if (!hoverTargetVisible) return null;
  return (
    <div
      aria-hidden="true"
      className="absolute inset-y-0 left-0 z-40 w-2"
      onPointerEnter={revealSidebar}
    />
  );
}

/**
 * The sidebar's body: the assistants and the conversations (D1). The side panel's tab an app with
 * navigation (an app's `sidebars`, its live sections, the browser's tabs), or an app's home, puts
 * "Conversations | <sections>" at its head (`useAppSidebarSide`).
 */
function SidebarBody() {
  const app = useAppSidebarSide(useLibraryPage().open);
  return (
    <>
      {app.toggle !== null && (
        <div className="shrink-0 px-3 pt-3" data-sidebar-side-toggle>
          {app.toggle}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pt-4 pb-3">
        {app.navigation}
        {!app.appShown && <WorkspaceBody />}
      </div>
    </>
  );
}

/** Where you are, in the ⌘K bar ("Crowdex Mobile / Navigation swipe gestures"). */
function useLocationLabel(): string | null {
  const { threads, activeThreadId } = useShellNavigation();
  const placements = useWorkspaceDirectory((state) => state.placements);
  const projects = useWorkspaceDirectory((state) => state.projects);
  const thread = threads.find((entry) => entry.id === activeThreadId);
  const ownProject = projects.find((project) => project.conversationId === activeThreadId);
  if (ownProject !== undefined) return `${ownProject.name} / ${ownProject.name}`;
  if (thread === undefined) return null;
  const projectId = placements[thread.id]?.projectId ?? null;
  const project = projects.find((entry) => entry.id === projectId);
  return project === undefined ? thread.title : `${project.name} / ${thread.title}`;
}

function TopRowButton({
  label,
  shortcut,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  shortcut?: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      aria-label={label}
      className="flex size-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-control hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 disabled:hover:bg-transparent"
      disabled={disabled}
      onClick={onClick}
      title={shortcut === undefined ? label : `${label} (${shortcut})`}
      type="button"
    >
      {children}
    </button>
  );
}

/**
 * The sidebar (D1): the top row (window controls, "Crowdex ⌄", notifications, hide; back and
 * forward are ⌘[ and ⌘]), ⌘K, the four favourites, the assistants and the conversations, New
 * conversation and the person's row.
 * Hidden (⌘S), it is revealed over the content from the window's left edge.
 */
export function DesktopSidebar({
  account,
  floating = false,
  onCollapse,
  onPointerEnter,
  onPointerLeave,
}: {
  account: { name: string; email: string; profile: string };
  /** Revealed over the content while hidden. */
  floating?: boolean;
  /** Collapses the bar to its rail (D2). */
  onCollapse: () => void;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
}) {
  // Its labels show the user's keys.
  useShortcutOverrides();
  const navigation = useShellNavigation();
  const macTitleBarInset = useDesktopShellStore((state) => state.macTitleBarInset);
  const location = useLocationLabel();
  const reconnect = useAccountsToReconnect(!navigation.preview);
  const { leave } = useLibraryPage();
  return (
    <aside
      aria-label="Sidebar"
      className={cn(
        'desktop-sidebar type-ui flex shrink-0 flex-col bg-desktop-sidebar',
        floating
          ? 'absolute inset-y-0 left-0 z-50 border-hairline border-r shadow-desktop-float'
          : 'relative border-hairline border-r',
      )}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      style={{ width: `${SIDEBAR_WIDTH}px` }}
    >
      <div
        className="flex h-12 min-w-0 shrink-0 items-center gap-1 pr-3"
        data-electron-drag-region
        data-sidebar-top-row
        style={{ paddingLeft: `${macTitleBarInset > 0 ? macTitleBarInset : 12}px` }}
      >
        <CrowlingoSwitcher />
        <span className="min-w-0 flex-1 self-stretch" data-electron-drag-region />
        <NotificationsButton />
        {/* ⌘S hides it altogether; this collapses it to its rail (D2). */}
        <TopRowButton label="Collapse to rail" onClick={onCollapse}>
          <PanelLeft className="size-4" />
        </TopRowButton>
      </div>
      <div className="flex shrink-0 flex-col gap-3 px-3 pt-1">
        <button
          className="flex h-9 items-center gap-2.5 rounded-xl bg-control/60 px-3 text-left text-muted-foreground transition-colors hover:bg-control hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => openShellSearch()}
          type="button"
        >
          <Search className="size-3.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate">
            {location ?? 'Search or start a conversation'}
          </span>
          <kbd className="type-caption shrink-0 font-sans text-subtle-foreground">
            {commandLabel('search', SIDEBAR_SEARCH_SHORTCUT)}
          </kbd>
        </button>
        <FavoriteTiles />
      </div>
      <SidebarBody />
      <div className="flex shrink-0 flex-col gap-2 px-3 pt-1 pb-3">
        <button
          className="flex h-9 w-full items-center gap-2.5 rounded-xl bg-control/60 px-3 text-left text-foreground transition-colors hover:bg-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => {
            setAllAppsOpen(false);
            leave();
            navigation.newTask();
          }}
          type="button"
        >
          <Plus className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">New conversation</span>
          <kbd className="type-caption shrink-0 font-sans text-subtle-foreground">
            {commandLabel('new-task', NEW_TASK_SHORTCUT)}
          </kbd>
        </button>
        {/* The person's row at the foot, as in every Crowlingo app. */}
        <div className="-mx-3 mt-1 border-hairline border-t px-3 pt-2" data-sidebar-account>
          <AccountMenu
            email={account.email}
            name={account.name}
            profile={account.profile}
            reconnect={reconnect}
            variant="row"
          />
        </div>
      </div>
    </aside>
  );
}

/**
 * The sidebar hidden: a button beside the window's controls shows it again, and the pointer on
 * the window's left edge reveals it over the content until it leaves.
 */
export function HiddenSidebar({
  account,
  macTitleBarInset,
  revealed,
  reveal,
  scheduleClose,
  onShow,
  onCollapse,
}: {
  account: { name: string; email: string; profile: string };
  macTitleBarInset: number;
  revealed: boolean;
  reveal: () => void;
  scheduleClose: () => void;
  onShow: () => void;
  onCollapse: () => void;
}) {
  // Its labels show the user's keys.
  useShortcutOverrides();
  return (
    <>
      <DesktopSidebarChrome hoverTargetVisible={!revealed} revealSidebar={reveal} />
      <button
        aria-label="Show sidebar"
        className="absolute top-2.5 z-40 flex size-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-control hover:text-foreground"
        data-electron-no-drag-region
        onClick={onShow}
        style={{ left: `${macTitleBarInset > 0 ? macTitleBarInset : 12}px` }}
        title={`Show sidebar (${commandLabel('toggle-sidebar', TOGGLE_SIDEBAR_SHORTCUT)})`}
        type="button"
      >
        <PanelLeft className="size-4" />
      </button>
      {revealed && (
        <DesktopSidebar
          account={account}
          floating
          onCollapse={onCollapse}
          onPointerEnter={reveal}
          onPointerLeave={scheduleClose}
        />
      )}
    </>
  );
}
